import React, { useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Animated,
  Dimensions,
  StatusBar,
  Platform,
  SafeAreaView,
  Image,
} from 'react-native';
import { COLORS } from '../constants/theme';

const { width, height } = Dimensions.get('window');

interface SplashScreenProps {
  onAnimationComplete: () => void;
}

export const SplashScreen: React.FC<SplashScreenProps> = ({ onAnimationComplete }) => {
  const logoScale = useRef(new Animated.Value(1)).current;
  const logoTranslateX = useRef(new Animated.Value(0)).current;
  const textOpacity = useRef(new Animated.Value(0)).current;
  const textTranslateX = useRef(new Animated.Value(20)).current;

  useEffect(() => {
    const animation = Animated.sequence([
      // First phase: logo moves left and gets smaller
      Animated.parallel([
        Animated.timing(logoTranslateX, {
          toValue: -100,
          duration: 1000,
          useNativeDriver: Platform.OS !== 'web',
        }),
        Animated.timing(logoScale, {
          toValue: 0.7,
          duration: 1000,
          useNativeDriver: Platform.OS !== 'web',
        }),
      ]),
      // Second phase: "Lifecycle" text appears
      Animated.parallel([
        Animated.timing(textOpacity, {
          toValue: 1,
          duration: 600,
          useNativeDriver: Platform.OS !== 'web',
        }),
        Animated.timing(textTranslateX, {
          toValue: 0,
          duration: 600,
          useNativeDriver: Platform.OS !== 'web',
        }),
      ]),
      // Pause to show the result
      Animated.delay(800),
    ]);

    animation.start(() => {
      onAnimationComplete();
    });
  }, []);

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar 
        barStyle="dark-content" 
        backgroundColor="#FFFFFF"
        translucent={Platform.OS === 'android'}
      />
      
      <View style={styles.content}>
        <Animated.View
          style={[
            styles.logoContainer,
            {
              transform: [
                { translateX: logoTranslateX },
                { scale: logoScale },
              ],
            },
          ]}
        >
          <Image source={require('../../assets/images/logo.png')} style={styles.logo} />
        </Animated.View>

        <Animated.View
          style={[
            styles.textContainer,
            {
              opacity: textOpacity,
              transform: [{ translateX: textTranslateX }],
            },
          ]}
        >
          <Text style={styles.appName}>Lifecycle</Text>
        </Animated.View>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  content: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
  },
  logoContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  logo: {
    width: 160,
    height: 120,
    resizeMode: 'contain',
  },
  logoCircle: {
    width: Platform.select({ ios: 60, android: 60, default: 50 }),
    height: Platform.select({ ios: 60, android: 60, default: 50 }),
    borderRadius: Platform.select({ ios: 30, android: 30, default: 25 }),
    backgroundColor: '#1E3A8A',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  logoInnerCircle: {
    width: Platform.select({ ios: 40, android: 40, default: 35 }),
    height: Platform.select({ ios: 40, android: 40, default: 35 }),
    borderRadius: Platform.select({ ios: 20, android: 20, default: 17.5 }),
    backgroundColor: '#3B82F6',
    position: 'absolute',
    right: Platform.select({ ios: 5, android: 5, default: 4 }),
    top: Platform.select({ ios: 5, android: 5, default: 4 }),
  },
  textContainer: {
    position: 'absolute',
    left: '50%',
    marginLeft: -40,
  },
  appName: {
    fontSize: 36,
    fontWeight: '900',
    color: COLORS.primary,
    letterSpacing: 1,
    fontFamily: 'Montserrat_700Bold',
  },
});
